import React, { useState, useEffect, useCallback } from 'react';

export interface DiagnosticStep {
  id: string;
  label: string;
  description?: string;
  check: () => Promise<DiagnosticResult> | DiagnosticResult;
  critical?: boolean;
  category?: string;
  dependencies?: string[];
}

export interface DiagnosticResult {
  passed: boolean;
  details?: any;
  error?: string;
  duration?: number;
  timestamp?: string;
}

export interface CheckState {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped';
  result?: DiagnosticResult;
  startTime?: number;
  endTime?: number;
}

interface ScienceDebuggerProps {
  title: string;
  description?: string;
  steps: DiagnosticStep[];
  autoRun?: boolean;
  animationDelay?: number;
  onComplete?: (results: CheckState[]) => void;
  showDetails?: boolean;
  groupByCategory?: boolean;
}

function classNames(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

export const ScienceDebugger: React.FC<ScienceDebuggerProps> = ({
  title,
  description,
  steps: rawSteps,
  autoRun = false,
  animationDelay = 300,
  onComplete,
  showDetails = true,
  groupByCategory = false,
}) => {
  // Ensure steps is always a valid array with valid items
  const steps = React.useMemo(() =>
    (rawSteps || []).filter(step => step && step.id && step.label && step.check),
    [rawSteps]
  );

  const [checks, setChecks] = useState<CheckState[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);

  // Initialize checks
  useEffect(() => {
    const initialChecks: CheckState[] = steps
      .filter(step => step && step.id)
      .map(step => ({
        id: step.id,
        label: step.label,
        status: 'pending',
      }));
    setChecks(initialChecks);
  }, [steps]);

  const runDiagnostics = useCallback(async () => {
    setIsRunning(true);
    setStartTime(Date.now());
    setEndTime(null);

    const results: CheckState[] = [];

    // Reset all checks to pending
    setChecks(steps
      .filter(step => step && step.id)
      .map(step => ({
        id: step.id,
        label: step.label,
        status: 'pending',
      })));

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (!step || !step.id) continue;

      // Check dependencies
      if (step.dependencies?.length) {
        const failedDeps = step.dependencies.filter(depId =>
          results.find(r => r.id === depId && r.status === 'failed')
        );

        if (failedDeps.length > 0) {
          results.push({
            id: step.id,
            label: step.label,
            status: 'skipped',
            result: {
              passed: false,
              error: `Skipped due to failed dependencies: ${failedDeps.join(', ')}`,
            },
          });

          setChecks(prev => [...results, ...steps.slice(i + 1).map(s => ({
            id: s.id,
            label: s.label,
            status: 'pending' as const,
          }))]);

          await new Promise(resolve => setTimeout(resolve, animationDelay));
          continue;
        }
      }

      // Update to running
      setChecks(prev => {
        const updated = [...prev];
        updated[i] = { ...updated[i], status: 'running', startTime: Date.now() };
        return updated;
      });

      // Small delay for animation
      await new Promise(resolve => setTimeout(resolve, animationDelay / 3));

      // Run the check
      try {
        const checkStartTime = performance.now();
        const result = await Promise.resolve(step.check());
        const duration = performance.now() - checkStartTime;

        const checkResult: CheckState = {
          id: step.id,
          label: step.label,
          status: result.passed ? 'passed' : 'failed',
          result: {
            ...result,
            duration,
            timestamp: new Date().toISOString(),
          },
          startTime: Date.now() - duration,
          endTime: Date.now(),
        };

        results.push(checkResult);

        setChecks(prev => {
          const updated = [...prev];
          updated[i] = checkResult;
          return updated;
        });
      } catch (error) {
        const errorResult: CheckState = {
          id: step.id,
          label: step.label,
          status: 'failed',
          result: {
            passed: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            timestamp: new Date().toISOString(),
          },
          endTime: Date.now(),
        };

        results.push(errorResult);

        setChecks(prev => {
          const updated = [...prev];
          updated[i] = errorResult;
          return updated;
        });
      }

      // Animation delay between checks
      await new Promise(resolve => setTimeout(resolve, animationDelay));
    }

    setEndTime(Date.now());
    setIsRunning(false);

    if (onComplete) {
      onComplete(results);
    }
  }, [steps, animationDelay, onComplete]);

  useEffect(() => {
    if (autoRun && steps.length > 0) {
      runDiagnostics();
    }
  }, [autoRun, steps.length]);

  const getStatusIcon = (status: CheckState['status']) => {
    switch (status) {
      case 'pending':
        return (
          <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-gray-100" />
        );
      case 'running':
        return (
          <svg className="w-6 h-6 animate-spin text-blue-600" viewBox="0 0 24 24">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              opacity="0.25"
            />
            <path
              d="M22 12a10 10 0 0 1-10 10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
          </svg>
        );
      case 'passed':
        return (
          <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 6L9 17l-5-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-6 h-6 text-red-600" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case 'skipped':
        return (
          <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 9l6 6m0-6l-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
    }
  };

  const getStatusColor = (status: CheckState['status']) => {
    switch (status) {
      case 'pending': return 'border-gray-200 bg-gray-50';
      case 'running': return 'border-blue-300 bg-blue-50 animate-pulse';
      case 'passed': return 'border-green-300 bg-green-50';
      case 'failed': return 'border-red-300 bg-red-50';
      case 'skipped': return 'border-gray-200 bg-gray-100';
    }
  };

  const passedCount = checks.filter(c => c.status === 'passed').length;
  const failedCount = checks.filter(c => c.status === 'failed').length;
  const skippedCount = checks.filter(c => c.status === 'skipped').length;
  const totalDuration = endTime && startTime ? endTime - startTime : null;

  // Group checks by category if requested
  const groupedChecks = groupByCategory
    ? steps.reduce((acc, step, index) => {
        const category = step?.category || 'General';
        if (!acc[category]) acc[category] = [];
        acc[category].push(checks[index]);
        return acc;
      }, {} as Record<string, CheckState[]>)
    : { 'All Checks': checks };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={runDiagnostics}
            disabled={isRunning}
            className={classNames(
              'px-4 py-2 rounded-lg font-medium transition-all',
              isRunning
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
            )}
          >
            {isRunning ? 'Running...' : 'Run Diagnostics'}
          </button>
        </div>

        {description && (
          <p className="text-gray-600 mb-4">{description}</p>
        )}

        {/* Progress Bar */}
        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
            style={{ width: `${(passedCount / steps.length) * 100}%` }}
          />
        </div>

        {/* Stats */}
        <div className="mt-4 flex gap-4 text-sm">
          <span className="text-green-600 font-medium">
            ✓ {passedCount} passed
          </span>
          <span className="text-red-600 font-medium">
            ✗ {failedCount} failed
          </span>
          {skippedCount > 0 && (
            <span className="text-gray-500 font-medium">
              ○ {skippedCount} skipped
            </span>
          )}
          {totalDuration && (
            <span className="text-gray-500 ml-auto">
              Total: {(totalDuration / 1000).toFixed(2)}s
            </span>
          )}
        </div>
      </div>

      {/* Checks List */}
      <div className="space-y-4">
        {Object.entries(groupedChecks).map(([category, categoryChecks]) => (
          <div key={category}>
            {groupByCategory && (
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{category}</h3>
            )}
            <div className="space-y-2">
              {categoryChecks.map((check) => {
                const step = steps.find(s => s.id === check.id);
                return (
                  <div
                    key={check.id}
                    className={classNames(
                      'rounded-lg border-2 p-4 transition-all duration-300',
                      getStatusColor(check.status)
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getStatusIcon(check.status)}
                      </div>

                      <div className="flex-grow">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {check.label}
                          </span>
                          {step && step.critical && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              Critical
                            </span>
                          )}
                          {check.result?.duration && (
                            <span className="text-xs text-gray-500 ml-auto">
                              {check.result.duration.toFixed(0)}ms
                            </span>
                          )}
                        </div>

                        {step && step.description && check.status === 'running' && (
                          <p className="text-sm text-gray-600 mt-1">
                            {step.description}
                          </p>
                        )}

                        {check.result?.error && (
                          <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                            Error: {check.result.error}
                          </div>
                        )}

                        {showDetails && check.result?.details && check.status !== 'pending' && (
                          <details className="mt-2">
                            <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                              Show details
                            </summary>
                            <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                              {JSON.stringify(check.result.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};